import {
  Component,
  Input,
  ViewChild,
  ElementRef,
  AfterViewInit,
  OnDestroy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  Chart,
  ChartConfiguration,
  ChartType,
  registerables,
  RadialLinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
  Legend,
} from 'chart.js';
import { getFilterColors } from '../../utils/filter-colors.util';
import { TranslationService } from '../../services/translation.service';
import { PerfectCorpResults } from '@server/entities/scan/perfectCorpResults';

Chart.register(
  ...registerables,
  RadialLinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
  Legend
);

@Component({
  selector: 'app-skin-radar-chart',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './skin-radar-chart.component.html',
  styleUrl: './skin-radar-chart.component.scss',
})
export class SkinRadarChartComponent implements AfterViewInit, OnDestroy {
  @Input() skinAnalysisData: PerfectCorpResults = null;
  @ViewChild('chartCanvas', { static: false })
  chartCanvas!: ElementRef<HTMLCanvasElement>;

  private chart?: Chart;

  constructor(private translationService: TranslationService) {}

  ngAfterViewInit() {
    if (
      this.skinAnalysisData &&
      typeof this.skinAnalysisData === 'object' &&
      Object.keys(this.skinAnalysisData).length > 0
    ) {
      setTimeout(() => {
        this.createChart();
      }, 100);
    }
  }

  ngOnDestroy() {
    if (this.chart) {
      this.chart.destroy();
    }
  }

  private createChart() {
    if (!this.chartCanvas || !this.chartCanvas.nativeElement) {
      return;
    }

    const ctx = this.chartCanvas.nativeElement.getContext('2d');
    if (!ctx) {
      return;
    }

    const chartData = this.prepareChartData();
    const pointColors =
      chartData.colors && chartData.colors.length > 0
        ? chartData.colors
        : getFilterColors(chartData.filterKeys);

    // Brand-aware chart colours (canvas can't read CSS vars, so resolve them
    // from the active brand at draw time — peritale pink / markable magenta).
    const brandCss = getComputedStyle(document.body);
    const brandRgb = brandCss.getPropertyValue('--brand-rgb').trim() || '255, 0, 131';
    const brandDarkerRgb =
      brandCss.getPropertyValue('--brand-darker-rgb').trim() || '183, 0, 93';
    const brandText = brandCss.getPropertyValue('--brand-text').trim() || '#990052';

    const viewportWidth = window.innerWidth;
    const isMobile = viewportWidth <= 480;
    const isTablet = viewportWidth > 480 && viewportWidth <= 768;

    const pointLabelFontSize = isMobile ? 9 : isTablet ? 10 : 12;
    const pointLabelPadding = isMobile ? 8 : isTablet ? 10 : 15;
    const tickFontSize = isMobile ? 9 : isTablet ? 10 : 11;

    const config: ChartConfiguration = {
      type: 'radar' as ChartType,
      data: {
        labels: chartData.labels,
        datasets: [
          {
            data: chartData.scores,
            backgroundColor: `rgba(${brandRgb}, 0.1)`,
            borderColor: `rgba(${brandRgb}, 0.4)`,
            borderWidth: 2,
            pointBackgroundColor: pointColors,
            pointBorderColor: `rgba(${brandRgb}, 0.6)`,
            pointBorderWidth: 1.5,
            pointRadius: 4,
            pointHoverRadius: 12,
            fill: true,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        elements: {
          line: {
            borderWidth: 3,
          },
        },
        scales: {
          r: {
            beginAtZero: true,
            max: 100,
            min: 0,
            ticks: {
              stepSize: 20,
              font: {
                size: tickFontSize,
                weight: 500,
              },
              color: `rgba(${brandDarkerRgb}, 0.6)`,
              backdropColor: 'transparent',
            },
            grid: {
              color: `rgba(${brandRgb}, 0.15)`,
              lineWidth: 1,
            },
            angleLines: {
              color: `rgba(${brandRgb}, 0.15)`,
              lineWidth: 1,
            },
            pointLabels: {
              font: {
                size: pointLabelFontSize,
                weight: 600,
              },
              color: brandText,
              padding: pointLabelPadding,
            },
          },
        },
        plugins: {
          legend: {
            display: false,
          },
          tooltip: {
            enabled: false,
            backgroundColor: 'rgba(0, 0, 0, 0.85)',
            titleColor: '#fff',
            bodyColor: '#fff',
            titleFont: {
              size: 13,
              weight: 600,
            },
            bodyFont: {
              size: 12,
            },
            cornerRadius: 8,
            position: 'average',
            caretPadding: 2,
            displayColors: false,
            callbacks: {
              label: (context) => {
                return `Score: ${context.parsed.r}/100`;
              },
            },
          },
        },
        interaction: {
          intersect: true,
          mode: 'point',
        },
      },
    };

    this.chart = new Chart(ctx, config);
  }

  private prepareChartData() {
    const filterMapping: { [key: string]: string } = {
      hd_acne: this.translationService.translate(
        'results.skinFilterViewer.filters.hd_acne'
      ),
      hd_wrinkle: this.translationService.translate(
        'results.skinFilterViewer.filters.hd_wrinkle'
      ),
      hd_pore: this.translationService.translate(
        'results.skinFilterViewer.filters.hd_pore'
      ),
      hd_moisture: this.translationService.translate(
        'results.skinFilterViewer.filters.hd_moisture'
      ),
      hd_redness: this.translationService.translate(
        'results.skinFilterViewer.filters.hd_redness'
      ),
      hd_age_spot: this.translationService.translate(
        'results.skinFilterViewer.filters.hd_age_spot'
      ),
      hd_dark_circle: this.translationService.translate(
        'results.skinFilterViewer.filters.hd_dark_circle'
      ),
      hd_firmness: this.translationService.translate(
        'results.skinFilterViewer.filters.hd_firmness'
      ),
      hd_texture: this.translationService.translate(
        'results.skinFilterViewer.filters.hd_texture'
      ),
      hd_radiance: this.translationService.translate(
        'results.skinFilterViewer.filters.hd_radiance'
      ),
      hd_oiliness: this.translationService.translate(
        'results.skinFilterViewer.filters.hd_oiliness'
      ),
      hd_eye_bag: this.translationService.translate(
        'results.skinFilterViewer.filters.hd_eye_bag'
      ),
      hd_droopy_upper_eyelid: this.translationService.translate(
        'results.skinFilterViewer.filters.hd_droopy_upper_eyelid'
      ),
      hd_droopy_lower_eyelid: this.translationService.translate(
        'results.skinFilterViewer.filters.hd_droopy_lower_eyelid'
      ),
    };

    const labels: string[] = [];
    const scores: number[] = [];
    const filterKeys: string[] = [];
    const colors: string[] = [];

    Object.keys(this.skinAnalysisData).forEach((key) => {
      if (key !== 'all' && key !== 'skin_age') {
        const filterData = this.skinAnalysisData[key];

        let scoreData = filterData;
        if (filterData && filterData.whole) {
          scoreData = filterData.whole;
        }

        if (scoreData && scoreData.ui_score !== undefined) {
          const label = filterData.name || filterMapping[key] || key;
          labels.push(label);
          scores.push(scoreData.ui_score);
          filterKeys.push(key);
          if (filterData.color) {
            colors.push(filterData.color);
          }
        }
      }
    });

    return { labels, scores, filterKeys, colors };
  }
}
